import { decode } from '@tsndr/cloudflare-worker-jwt';

let refreshPromise: Promise<void> | null = null;

function decodeExp(token?: string): number | null {
  if (!token) return null;
  try {
    const { payload }: any = decode(token);
    return typeof payload?.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

function authRedirectPath(): string {
  const { session } = useUserSession();
  const provider = (session.value as any)?.provider as string | undefined;
  return provider ? `/auth/${provider}` : '/auth';
}

async function ensureFreshToken(
  force: boolean = false,
  allowAnonymous: boolean = false
): Promise<void> {
  const { session, fetch: fetchSession, clear } = useUserSession();
  const id = (session.value as any)?.tokens?.id_token as string | undefined;
  const exp = decodeExp(id);
  const now = Math.floor(Date.now() / 1000);
  const needs = force || !exp || exp - now < 60;
  if (!needs) return;

  const refresh = (session.value as any)?.tokens?.refresh_token;
  if (!refresh) {
    if (!id && !allowAnonymous) throw new Error('No tokens available');
    return;
  }

  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        await $fetch('/api/jwt/refresh', { method: 'POST' });
        await fetchSession();
      } catch (e) {
        // Refresh failed: clear & redirect to force full OAuth
        await clear();
        return void navigateTo(authRedirectPath());
      } finally {
        refreshPromise = null;
      }
    })();
  }

  await refreshPromise;
}

export function useApi<T>(url: string, options?: any) {

  const { requestApiUrl } = useApiUrl();
  const { session, clear: clearSession } = useUserSession();
  const { site } = useSite();

  const getIdToken = () =>
    (session.value as any)?.tokens?.id_token as string | undefined;
  const getRefreshToken = () =>
    (session.value as any)?.tokens?.refresh_token as string | undefined;
  const retriedOnce = { value: false };

  return useFetch<T>(url, {
    baseURL: requestApiUrl.value,
    ...options,
    async onRequest({ options: requestOptions }) {
      if (!options?.allowAnonymous) {
        await ensureFreshToken(false, !!options?.allowAnonymous);
      }
      const idToken = getIdToken();
      if (!idToken && !options?.allowAnonymous) {
        throw new Error('No ID token available after refresh attempt');
      }
      requestOptions.headers = new Headers(requestOptions.headers);
      if (idToken) {
        requestOptions.headers.set('Authorization', `Bearer ${idToken}`);
      }
      requestOptions.headers.set(
        'x-site-id',
        site.value?.id || '00000000-0000-0000-0000-000000000000'
      );
      if (
        requestOptions.body &&
        typeof requestOptions.body === 'object' &&
        !(requestOptions.body instanceof FormData)
      ) {
        requestOptions.body = JSON.stringify(requestOptions.body);
      }
    },
    async onResponseError(ctx) {
      const { response, options: reqOptions } = ctx as any;
      if (response.status === 401 && !retriedOnce.value && getRefreshToken()) {
        retriedOnce.value = true;
        try {
          await ensureFreshToken(true, !!reqOptions?.allowAnonymous);
          const newId = getIdToken();
          if (newId) {
            const retryHeaders = new Headers(reqOptions.headers || {});
            retryHeaders.set('Authorization', `Bearer ${newId}`);
            retryHeaders.set(
              'x-site-id',
              site.value?.id || '00000000-0000-0000-0000-000000000000'
            );
            const data = await $fetch<T>(url, {
              baseURL: requestApiUrl.value,
              ...reqOptions,
              headers: retryHeaders,
            });
            (response as any)._data = data;
            response.status = 200;
            return;
          }
        } catch (e) {
          console.warn('[useApi] Retry after forced refresh failed', e);
          await clearSession();
          void navigateTo(authRedirectPath());
          throw new Error('Redirecting to auth');
        }
      }
      const message =
        response._data?.detail ||
        response._data?.title ||
        response.statusText ||
        'API request failed';
      const error: any = new Error(message);
      error.data = response._data;
      error.status = response.status;
      throw error;
    },
  });
}

export async function useApiFetch<T>(url: string, options?: any): Promise<T> {
  const { requestApiUrl } = useApiUrl();
  const { session, clear: clearSession } = useUserSession();
  const { site } = useSite();

  const getIdToken = () =>
    (session.value as any)?.tokens?.id_token as string | undefined;
  const getRefreshToken = () =>
    (session.value as any)?.tokens?.refresh_token as string | undefined;

  if (!options?.allowAnonymous) {
    await ensureFreshToken(false, !!options?.allowAnonymous);
  }
  const headers = new Headers(options?.headers || {});
  const idToken = getIdToken();
  if (idToken) {
    headers.set('Authorization', `Bearer ${idToken}`);
  }
  headers.set(
    'x-site-id',
    site.value?.id || '00000000-0000-0000-0000-000000000000'
  );

  const doFetch = async () =>
    await $fetch<T>(requestApiUrl.value + url, { ...options, headers });

  try {
    return await doFetch();
  } catch (err: any) {
    if (err?.status === 401 && getRefreshToken()) {
      try {
        await ensureFreshToken(true, !!options?.allowAnonymous);
        const newId = getIdToken();
        if (newId) {
          headers.set('Authorization', `Bearer ${newId}`);
          return await doFetch();
        }
      } catch (e) {
        await clearSession();
        void navigateTo(authRedirectPath());
        throw new Error('Redirecting to auth');
      }
    }
    throw err;
  }
}
